import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  ArrowUpRight,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";
const GALLERY_ITEMS = [
    { src: IMAGES.heroShowroom, h: "aspect-[4/3]", label: "Premium Showroom" },
    { src: IMAGES.manufacturing, h: "aspect-[3/4]", label: "Factory Floor · Morbi" },
    { src: IMAGES.steelRack, h: "aspect-square", label: "Slider Display Stand" },
    { src: IMAGES.blackFramework, h: "aspect-[3/4]", label: "Booklet Display Stand" },
    { src: IMAGES.tileTexture, h: "aspect-[4/3]", label: "Showroom Detail" },
    { src: IMAGES.architecture, h: "aspect-[3/4]", label: "Wall Display System" },
    { src: IMAGES.manufacturingProcess, h: "aspect-square", label: "Welding Bay" },
    { src: IMAGES.heroAlt, h: "aspect-[4/3]", label: "Large Format Tile Display" },
  ];
export default function GallerySection() {
    
  const [gIdx, setGIdx] = useState(null);
  const gOpen = gIdx !== null;
  const touchStartX = useRef(0);
  useEffect(() => {
    if (!gOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setGIdx(null);
      else if (e.key === "ArrowRight") setGIdx((i) => (i + 1) % GALLERY_ITEMS.length);
      else if (e.key === "ArrowLeft") setGIdx((i) => (i - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [gOpen]);

  return(
    <>
  <section data-testid="gallery-strip" className="relative py-24 md:py-36 bg-white">
            {/* GALLERY STRIP - Pinterest Masonry */}
            <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <Reveal>
                <div className="overline text-stenzia-green"><span className="kbd-line" /> Factory &amp; Showroom</div>
                <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tighter text-charcoal leading-[1.02]">Inside our world.</h2>
                </Reveal>
                <Reveal delay={1}>
                <Link to="/gallery" data-testid="gallery-view-all" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-charcoal text-sm font-medium border border-charcoal/15 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_14px_30px_-12px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 hover:border-charcoal/30 transition-all duration-400">
                    View full gallery
                    <ArrowUpRight size={16} className="transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
                </Reveal>
            </div>

            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
                {GALLERY_ITEMS.map((g, i) => (
                <Reveal key={i} delay={i % 4} className="break-inside-avoid">
                    <button
                    type="button"
                    onClick={() => setGIdx(i)}
                    aria-label={`Open ${g.label}`}
                    data-testid={`home-gallery-item-${i}`}
                    className={`gallery-card relative overflow-hidden rounded-2xl ${g.h} group block w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-stenzia-gold focus-visible:ring-offset-2`}
                    >
                    <img
                        src={g.src}
                        alt={g.label}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[450ms] ease-[cubic-bezier(.2,.7,.2,1)] will-change-transform group-hover:scale-[1.07]"
                    />
                    {/* Premium dark gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/75 via-charcoal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-[420ms] pointer-events-none" />
                    {/* Centered circular emerald arrow */}
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#0B6B53] text-white flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(11,107,83,0.7)] opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 group-hover:[transform:translate(-50%,-50%)_scale(1)] transition-all duration-[420ms] ease-[cubic-bezier(.2,.7,.2,1)] pointer-events-none">
                        <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:rotate-0" strokeWidth={2.2} />
                    </span>
                    {/* Label */}
                    <span className="absolute bottom-4 left-4 right-4 text-left text-white font-display text-sm md:text-base tracking-tight opacity-0 translate-y-1.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-[420ms] pointer-events-none">
                        {g.label}
                    </span>
                    </button>
                </Reveal>
                ))}
            </div>
            </div>
        </section>
        
        <AnimatePresence>
          {gOpen && (
            <motion.div
              data-testid="home-gallery-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setGIdx(null)}
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                if (Math.abs(dx) > 50) {
                  if (dx < 0) setGIdx((i) => (i + 1) % GALLERY_ITEMS.length);
                  else setGIdx((i) => (i - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length);
                }
              }}
              className="fixed inset-0 z-[70] bg-charcoal/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
            >
              <button
                data-testid="home-gallery-close"
                className="absolute top-5 right-5 md:top-6 md:right-6 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white text-white hover:text-charcoal flex items-center justify-center transition-all"
                onClick={(e) => { e.stopPropagation(); setGIdx(null); }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
              <button
                data-testid="home-gallery-prev"
                onClick={(e) => { e.stopPropagation(); setGIdx((i) => (i - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length); }}
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-stenzia-gold text-white hover:text-charcoal flex items-center justify-center transition-all"
                aria-label="Previous"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="home-gallery-next"
                onClick={(e) => { e.stopPropagation(); setGIdx((i) => (i + 1) % GALLERY_ITEMS.length); }}
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-stenzia-gold text-white hover:text-charcoal flex items-center justify-center transition-all"
                aria-label="Next"
              >
                <ChevronRight size={20} />
              </button>
              <motion.div
                key={gIdx}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
                className="max-w-5xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img src={GALLERY_ITEMS[gIdx].src} alt={GALLERY_ITEMS[gIdx].label} className="w-full max-h-[78vh] object-contain rounded-2xl shadow-2xl" />
                <div className="mt-4 flex items-center justify-between text-white/80 text-sm">
                  <div className="overline text-stenzia-gold">{String(gIdx + 1).padStart(2, "0")} / {String(GALLERY_ITEMS.length).padStart(2, "0")}</div>
                  <div className="font-display text-base">{GALLERY_ITEMS[gIdx].label}</div>
                </div>
              </motion.div>
            </motion.div>
          )}
                 </AnimatePresence>
         </>
     );
 }
